import type { CommerceShippingProduct } from '@/pages/BusinessCommerces/services/commerceShippings.service';
import { ShippingsFilter } from '@/pages/Business/pages/Shippings/models';
import { externalOperatorService } from './externalOperatorService';
import { PaginatedRequest, PaginatedResponse } from '@/types';

export interface ExternalOperatorShippingZone {
  zoneId: string;
  zoneName: string | null;
  zoneTypeId: string;
  zoneTypeName: string | null;
}

export interface ExternalOperatorShippingListItem {
  id: number;
  externalId: string | null;
  commerceId: string | null;
  commerce: string | null;
  branch: string | null;
  service: string | null;
  recipient: string;
  recipientPhoneNumber: string | null;
  deliveryAddress: string | null;
  createdAt: string | null;
  dateToDelivery: string | null;
  dateDelivered: string | null;
  status: string | null;
  statusId: string | null;
  logisticOperator: string | null;
  logisticOperatorId: string | null;
  statusName: string | null;
  lastDriverName: string | null;
  zones: ExternalOperatorShippingZone[];
  timeSlot: { code: string | null; from: string | null; to: string | null } | null;
}

export interface ExternalOperatorShippingServiceWithOperator {
  id: string;
  name: string | null;
  commercialName: string | null;
  deliverySlaHours?: number | null;
}

export interface ExternalOperatorShippingDetail {
  id: number;
  externalId: string | null;
  statusId: string | null;
  status: string | null;
  logisticOperator: string | null;
  logisticOperatorId: string | null;
  serviceWithOperatorId?: string | null;
  serviceWithOperator?: ExternalOperatorShippingServiceWithOperator | string | null;
  commerce: string | null;
  branch: string | null;
  recipient: string;
  recipientPhoneNumber: string | null;
  deliveryAddress: string | null;
  deliveryLat: string | null;
  deliveryLong: string | null;
  deliveryDetails: string | null;
  originAddress: string | null;
  originLat: string | null;
  originLong: string | null;
  pickupDetails: string | null;
  invoiceNumber: string | null;
  timeSlot: { code: string | null; from: string | null; to: string | null } | null;
  size: string | null;
  service: string | null;
  createdAt: string | null;
  dateToDelivery: string | null;
  dateDelivered: string | null;
  products: CommerceShippingProduct[];
  zones: ExternalOperatorShippingZone[];
  lastDriverName: string | null;
}

export type GetExternalOperatorShippingsArgs = {
  filters: ShippingsFilter;
  paginated: PaginatedRequest;
};

const joinIds = (ids?: string[]) =>
  ids && ids.length > 0 ? ids.join(',') : undefined;

const trimmed = (value?: string | null) => {
  const result = value?.trim();
  return result ? result : undefined;
};

const isoDate = (value?: Date | null) =>
  value ? new Date(value).toISOString() : undefined;

const deliveryDay = (value?: Date | null) => {
  if (!value) return undefined;
  const date = new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}T12:00:00.000Z`;
};

const externalOperatorShippingsApi = externalOperatorService.injectEndpoints({
  endpoints: (builder) => ({
    getExternalOperatorShippings: builder.query<
      PaginatedResponse<ExternalOperatorShippingListItem>,
      GetExternalOperatorShippingsArgs
    >({
      query: ({ paginated, filters }) => {
        const base = {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
        };

        const params: Record<string, string | number> = { ...base };
        const optional: Record<string, string | number | undefined> = {
          number: trimmed(filters.number),
          commerceShippingNumber: trimmed(filters.commerceShippingNumber),
          statusId: filters.statusId,
          commerceId: filters.commerceId,
          branchId: filters.branchId,
          recipient: trimmed(filters.recipient),
          destinationAddress: trimmed(filters.destinationAddress),
          cityIds: joinIds(filters.cityIds),
          destinationStateOrDepartmentIds: joinIds(
            filters.destinationStateOrDepartmentIds
          ),
          serviceWithOperatorIds: joinIds(filters.serviceWithOperatorIds),
          timeSlotIds: joinIds(filters.timeSlotIds),
          dateFrom: isoDate(filters.createdAtFrom),
          dateTo: isoDate(filters.createdAtTo),
          deliveryDateFrom: deliveryDay(filters.deliveryDateFrom),
          deliveryDateTo: deliveryDay(filters.deliveryDateTo),
          dateDeliveredFrom: isoDate(filters.dateDeliveredFrom),
          dateDeliveredTo: isoDate(filters.dateDeliveredTo),
        };

        Object.entries(optional).forEach(([key, value]) => {
          if (value !== undefined && value !== '') params[key] = value;
        });

        return { url: '/external-operator/shippings', params };
      },
      providesTags: ['ExternalOperatorShippings'],
    }),

    getExternalOperatorShippingByNumber: builder.query<
      ExternalOperatorShippingDetail,
      { number: string }
    >({
      query: ({ number }) => ({ url: `/external-operator/shippings/${number}` }),
      providesTags: (_result, _error, { number }) => [
        { type: 'ExternalOperatorShippings', id: number },
        'ExternalOperatorShippings',
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetExternalOperatorShippingsQuery,
  useGetExternalOperatorShippingByNumberQuery,
  useLazyGetExternalOperatorShippingByNumberQuery,
} = externalOperatorShippingsApi;
